import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';

const card = { background: 'var(--surface)', border: '1px solid #e5e7eb', borderRadius: 14, padding: 18, marginBottom: 16 };
const nf = (n) => (n || 0).toLocaleString('it-IT');

const LUCCHETTO_HEX = {
  GRIGIO: '#9ca3af', VERDE: '#16a34a', NERO: '#111827', VIOLA: '#7c3aed',
  ROSSO: '#dc2626', GIALLO: '#eab308', ARANCIONE: '#f97316', 'NON TROVATO': '#cbd5e1',
};

// AI SSMATE: verifica massiva del presidio commerciale TIM sui numeri con CF valido,
// saltando quelli già verificati entro il timing. Apre Edge in background (un'azienda per volta).
export default function SsmateAgenteCard() {
  const [info, setInfo] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const jobIdRef = useRef(null);

  const loadPreview = useCallback(() => {
    axios.get(`${API}/ssmate/bulk/preview`).then((r) => {
      setInfo(r.data);
      if (r.data.job) { jobIdRef.current = r.data.job.id; setJob(r.data.job); }
    }).catch(() => {});
  }, []);

  const loadJob = useCallback(() => {
    if (!jobIdRef.current) return;
    axios.get(`${API}/ssmate/job/${jobIdRef.current}`).then((r) => {
      setJob(r.data);
      if (['completed', 'cancelled'].includes(r.data.status)) { jobIdRef.current = null; loadPreview(); }
    }).catch(() => { jobIdRef.current = null; });
  }, [loadPreview]);

  useEffect(() => { loadPreview(); }, [loadPreview]);
  useEffect(() => {
    const t = setInterval(() => { if (jobIdRef.current) loadJob(); else loadPreview(); }, 4000);
    return () => clearInterval(t);
  }, [loadJob, loadPreview]);

  const avvia = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ssmate/bulk`);
      if (r.data.job_id) { jobIdRef.current = r.data.job_id; toast.success(`AI SSMATE avviato su ${nf(r.data.total)} aziende`); loadJob(); }
      else toast.info(r.data.message || 'Nessuna azienda da verificare');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore avvio agente'); }
    setBusy(false);
  };
  const ferma = async () => {
    if (!jobIdRef.current) return;
    try { await axios.post(`${API}/ssmate/job/${jobIdRef.current}/cancel`); toast.info('Arresto agente in corso…'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const running = job && ['running', 'pending'].includes(job.status);
  const pct = job && job.total ? Math.round((100 * job.completed) / job.total) : 0;

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>AI SSMATE — presidio commerciale TIM</div>
          {info?.agente_attivo && info?.in_attesa && (
            <span className="agente-attesa" style={{ fontSize: 12.5, color: '#b45309', fontWeight: 700 }}>
              ● in attesa di record{info.motivo_attesa ? ` (${info.motivo_attesa})` : ''}
            </span>
          )}
        </div>
        {running
          ? <button className="btn" style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', fontWeight: 700 }} onClick={ferma}>Ferma agente</button>
          : <button className="btn btn-primary" onClick={avvia} disabled={busy || !info}>Avvia agente</button>}
      </div>

      <div style={{ fontSize: 13, color: '#64748b', margin: '8px 0 12px' }}>
        Verifica le aziende con <b>Codice Fiscale valido</b> non verificate da SSMATE
        {info?.timing_enabled ? <> da almeno <b>{info?.timing_giorni} giorni</b></> : <> (timing disattivato)</>}.
        Apre Edge in background, una per volta; rispetta la quota e ruota gli utenti.
      </div>

      {!running ? (
        <div style={{ fontSize: 14 }}>
          Aziende verificabili ora: <b style={{ fontSize: 18 }}>{nf(info?.verificabili)}</b>
          {info?.esauriti?.length > 0 && (
            <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>Utenti esauriti oggi: {info.esauriti.join(', ')}</div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ height: 10, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)', transition: 'width .4s' }} />
          </div>
          <div style={{ fontSize: 13, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <span><b>{nf(job.completed)}</b> / {nf(job.total)} ({pct}%)</span>
            <span style={{ color: '#15803d' }}>Verificati: <b>{nf(job.verificati)}</b></span>
            <span style={{ color: '#94a3b8' }}>Saltati: <b>{nf(job.saltati)}</b></span>
            <span style={{ color: job.errori ? '#b91c1c' : '#94a3b8' }}>Errori: <b>{nf(job.errori)}</b></span>
          </div>
          {job.current && <div style={{ fontSize: 12.5, color: '#475569', marginTop: 6 }}>In corso: <b>{job.current}</b> · utente {job.current_user}</div>}
          {job.site_status && <div style={{ fontSize: 12.5, color: '#b45309', marginTop: 4 }}>{job.site_status}</div>}
          {job.quota_stopped && <div style={{ fontSize: 12.5, color: '#b45309', marginTop: 4 }}>Fermato: quota giornaliera esaurita.</div>}
          {Object.keys(job.lucchetti || {}).length > 0 && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, fontSize: 12.5 }}>
              {Object.entries(job.lucchetti).map(([k, v]) => (
                <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: LUCCHETTO_HEX[k] || '#94a3b8', display: 'inline-block' }} />
                  {k}: <b>{nf(v)}</b>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
